import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Lock, Landmark, User, Phone, Globe, FileText, Info, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = { navy: '#2B4C7E', gold: '#C8A97E', cream: '#FDFBF7', green: '#7FA98C', ink: '#1C252E', mute: '#8D8880', line: '#E3E1DC' };
const inp = 'w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none focus:border-[#2B4C7E]';
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function BeneficiaryAccess() {
  const [token, setToken] = useState('');
  const [info, setInfo] = useState(null);

  const loadInfo = async (t) => {
    try { const r = await axios.get(`${API}/beneficiary/released-info`, { headers: { Authorization: `Bearer ${t}` } }); setInfo(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'No released information available.'); }
  };

  if (!token || !info) return <Login onAuth={(t) => { setToken(t); loadInfo(t); }} />;

  return (
    <div className="min-h-screen overflow-x-hidden py-8 px-4" style={{ background: C.cream }} data-testid="beneficiary-page">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3" style={{ background: C.navy }}><FileText className="h-7 w-7 text-white" /></div>
          <h1 className="font-display text-2xl font-bold" style={{ color: C.ink }}>Released legacy information</h1>
          <p className="text-sm mt-1" style={{ color: C.mute }}>From {info.owner_name}’s EasyPassing account</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#EDE7DC] p-6 space-y-4">
          {info.executor && (
            <Row icon={User} label="Primary Executor" value={<>{info.executor.name}{info.executor.email && <div className="text-xs" style={{ color: C.mute }}>{info.executor.email}{info.executor.phone ? ` · ${info.executor.phone}` : ''}</div>}</>} />
          )}
          {info.bank_name && <Row icon={Landmark} label="Bank" value={info.bank_name} />}
          {info.bank_contact && (info.bank_contact.phone || info.bank_contact.website) && (
            <Row icon={Phone} label="Bank contact" value={<div className="flex flex-col gap-1">{info.bank_contact.phone && <a href={`tel:${info.bank_contact.phone}`} style={{ color: C.navy }}>{info.bank_contact.phone}</a>}{info.bank_contact.website && <a href={info.bank_contact.website} target="_blank" rel="noreferrer" className="flex items-center gap-1" style={{ color: C.navy }}><Globe className="h-3.5 w-3.5" /> Website</a>}</div>} />
          )}
          {info.masked_account && <Row icon={Lock} label="Account" value={info.masked_account} />}
          {info.distribution_type === 'percentage' && info.percentage_amount != null && <Row icon={Check} label="Intended share" value={`${info.percentage_amount}%`} />}
          {info.distribution_type === 'fixed_amount' && info.fixed_amount != null && <Row icon={Check} label="Intended amount" value={money(info.fixed_amount)} />}
          <div>
            <p className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: C.mute }}>Instructions</p>
            <p className="text-sm" style={{ color: C.ink }}>“{info.instructions}”</p>
          </div>
        </div>

        <div className="rounded-2xl border p-4 mt-4" style={{ background: '#FFF9EC', borderColor: '#F0E2C0' }}>
          <p className="text-xs flex gap-2" style={{ color: '#7a6526' }}><Info className="h-4 w-4 shrink-0" /> {info.disclaimer}</p>
        </div>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#EBF0F5' }}><Icon className="h-4.5 w-4.5" style={{ color: C.navy }} /></div>
    <div className="min-w-0"><p className="text-xs font-bold uppercase tracking-wide" style={{ color: C.mute }}>{label}</p><div className="text-sm font-medium" style={{ color: C.ink }}>{value}</div></div>
  </div>
);

function Login({ onAuth }) {
  const [email, setEmail] = useState(''); const [code, setCode] = useState(''); const [sent, setSent] = useState(false); const [busy, setBusy] = useState(false);
  const req = async () => { if (!email) return; setBusy(true); try { await axios.post(`${API}/beneficiary/request-code`, { email }); setSent(true); toast.success('If information has been released for you, a code was emailed.'); } catch { toast.error('Could not send code'); } finally { setBusy(false); } };
  const verify = async () => { setBusy(true); try { const r = await axios.post(`${API}/beneficiary/verify-code`, { email, code }); onAuth(r.data.token); } catch (e) { toast.error(e?.response?.data?.detail || 'Invalid code'); } finally { setBusy(false); } };
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: C.cream }} data-testid="beneficiary-login">
      <div className="bg-white rounded-3xl border border-[#EDE7DC] p-8 w-full max-w-md">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: C.navy }}><Lock className="h-6 w-6 text-white" /></div>
        <h1 className="font-display text-2xl font-bold" style={{ color: C.ink }}>Secure beneficiary portal</h1>
        <p className="text-sm mt-1 mb-5" style={{ color: C.mute }}>Enter the email where you received your notification. We’ll email a secure code to verify you.</p>
        <label className="block text-xs font-semibold mb-1" style={{ color: '#57534E' }}>Email</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} className={inp} data-testid="ben-email" placeholder="you@example.com" />
        {!sent ? (
          <button onClick={req} disabled={busy} className="mt-4 w-full h-11 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: C.navy }} data-testid="ben-send-code">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Send code</button>
        ) : (
          <>
            <label className="block text-xs font-semibold mb-1 mt-4" style={{ color: '#57534E' }}>6-digit code</label>
            <input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} className={inp} data-testid="ben-code" placeholder="123456" />
            <button onClick={verify} disabled={busy} className="mt-4 w-full h-11 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: C.navy }} data-testid="ben-verify">{busy && <Loader2 className="h-4 w-4 animate-spin" />} View information</button>
            <button onClick={req} className="mt-2 w-full text-xs" style={{ color: C.mute }}>Resend code</button>
          </>
        )}
      </div>
    </div>
  );
}
